/*
Purpose: Pending Payment with Late Fee — select specific pending invoices via
         checkbox (not "pay all"), type a late fee per invoice or one common
         value applied to every selected row, see the live total due, then pay
         the exact amount in one go. Payment is added to the wallet and
         immediately allocated across the selected invoices (oldest due date
         first), each invoice marked as Paid, late fee recorded, wallet left at $0.
Developer: BY KTH
Date: 07/09/2026
Project: TCMS
*/

import { useState, useEffect, useMemo } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";

import Breadcrumbs from "@mui/material/Breadcrumbs";
import Typography from "@mui/material/Typography";
import { Button as Buttonmui } from "@mui/material";
import LinearProgress from "@mui/material/LinearProgress";
import { ToastContainer, toast } from "react-toastify";

import axios from "axios";
import Moment from "moment";
import Select from "react-select";
import { security_key, BASE_URL } from "../global";
import { NavLink, useLocation } from "react-router-dom";
// pp_pay_method / payment_method convention used across this project's
// reports pages: 1 = Online, 2 = Offline.

function generateTransactionRef() {
  // Client-side idempotency key (step 14). Prefer the browser's crypto UUID
  // when available; fall back to a timestamp+random string otherwise.
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return "PPLF-" + crypto.randomUUID();
  }
  return "PPLF-" + Date.now() + "-" + Math.random().toString(36).slice(2, 10);
}

function toNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ReportFinancePendingPaymentLateFee() {


  const location = useLocation();
  const [lockedStudent, setLockedStudent] = useState(false);

  const paymentMethodOptions = [
    { value: "1", label: "Online" },
    { value: "2", label: "Offline" },
  ];

  const currency =
    (+sessionStorage.getItem("currency_code") != null && sessionStorage.getItem("currency_code") != ""
      ? sessionStorage.getItem("currency_code")
      : "SGD");

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [invoices, setInvoices] = useState([]);

  

  // pk_ps_key -> true for selected rows
  const [selected, setSelected] = useState({});
  // pk_ps_key -> late fee string value (kept as string for controlled input UX)
  const [lateFees, setLateFees] = useState({});

  // "Common Late Fee" bulk-apply input (Requirement #2: type once, applies
  // to every currently-selected invoice's late fee input).
  const [bulkLateFee, setBulkLateFee] = useState("");

  // --- Student filter (mirrors the pattern used on the Failed Payments report) ---
  const [studentOptions, setStudentOptions] = useState([]);
  const [filterStudent, setFilterStudent] = useState(null);
  const [filterPaymentMethod, setFilterPaymentMethod] = useState(null);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

    const filteredInvoices = useMemo(() => {
  return invoices.filter((row) => {
    // Payment Method filter — matches against pp_pay_method column (1 = Online, 2 = Offline)
    if (filterPaymentMethod && String(row.pp_pay_method) !== String(filterPaymentMethod.value)) {
      return false;
    }

    // Date range filter — matches against invoice due date (ps_date)
    if (filterStartDate && row.ps_date) {
      if (Moment(row.ps_date, "YYYY-MM-DD").isBefore(Moment(filterStartDate, "YYYY-MM-DD"), "day")) {
        return false;
      }
    }
    if (filterEndDate && row.ps_date) {
      if (Moment(row.ps_date, "YYYY-MM-DD").isAfter(Moment(filterEndDate, "YYYY-MM-DD"), "day")) {
        return false;
      }
    }

    return true;
  });
}, [invoices, filterPaymentMethod, filterStartDate, filterEndDate]);



  // --- Confirm & pay modal ---
  const [showConfirm, setShowConfirm] = useState(false);
  const [paymentRef, setPaymentRef] = useState(null); // stable across retries of the same batch

  // Payment collection method chosen in the confirm modal.
  // "online"  -> charges the student's card on file via Stripe (server-side).
  // "offline" -> staff records payment mode + transaction ID.
  const [paymentMethod, setPaymentMethod] = useState("offline");
  const [offlineModesOptions, setOfflineModesOptions] = useState([]);
  const [offlinePkCpmKey, setOfflinePkCpmKey] = useState(null); // react-select value
  const [offlineTransactionId, setOfflineTransactionId] = useState("");

  // --- Process Log viewer (top-right button) ---
  const [showProcessLog, setShowProcessLog] = useState(false);
  const [processLogs, setProcessLogs] = useState([]);
  const [loadingProcessLog, setLoadingProcessLog] = useState(false);

  const fetchOfflineModesOptions = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getOfflinePaymentModeCenter",
        fk_cent_key: sessionStorage.getItem("key"),
      });
      if (response.data.success === true) {
        const formattedOptions = response.data.data.map((option) => ({
          value: option.pk_cpm_key,
          label: option.pm_name,
        }));
        setOfflineModesOptions(formattedOptions);
      }
    } catch (error) {
      console.error("Error fetching payment modes:", error);
    }
  };

  const fetchProcessLogs = async (studentKey) => {
    setLoadingProcessLog(true);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getPaymentProcessLogCenter",
        fk_cent_key: sessionStorage.getItem("key"),
        fk_student_key: studentKey || "",
      });
      if (response.data.success === true) {
        setProcessLogs(response.data.data || []);
      } else {
        setProcessLogs([]);
      }
    } catch (error) {
      console.error("Error fetching payment process log:", error);
      setProcessLogs([]);
    } finally {
      setLoadingProcessLog(false);
    }
  };

  const openProcessLog = () => {
    // Uses whatever student is currently selected in the page's own filter,
    // so the log view and the invoice list are always looking at the same student.
    fetchProcessLogs(filterStudent?.value || null);
    setShowProcessLog(true);
  };

  useEffect(() => {
    // Keep the log list in sync if it's open and the student filter changes.
    if (showProcessLog) {
      fetchProcessLogs(filterStudent?.value || null);
    }
  }, [filterStudent]);

  const fetchStudentOptions = async () => {
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getStudentListForCentre",
        fk_cent_key: sessionStorage.getItem("key"),
      });
      if (response.data.success === true) {
        const options = response.data.data.map((s) => ({
          value: s.pk_student_key,
          label: `${s.st_first_name || ""} ${s.st_surname || ""}`.trim() || "Unknown",
        }));
        setStudentOptions(options);
      }
    } catch (error) {
      console.error("Error fetching student list:", error);
    }
  };

  const fetchInvoices = async (studentKey) => {
    setLoading(true);
    try {
      const response = await axios.post(BASE_URL + "API/api_index.php", {
        security_key: security_key,
        mode: "getPendingInvoicesForLateFeePaymentCenter",
        fk_cent_key: sessionStorage.getItem("key"),
        fk_student_key: studentKey || "",
      });

      if (response.data.success === true) {
        setInvoices(response.data.data || []);
      } else {
        setInvoices([]);
      }
    } catch (error) {
      console.error("Error fetching pending invoices:", error);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   fetchStudentOptions();
  //   fetchOfflineModesOptions();
  //   fetchInvoices(null);
  // }, []);
  useEffect(() => {
    fetchStudentOptions();
    fetchOfflineModesOptions();
    if (!location.state?.studentKey) {
      fetchInvoices(null);
    }
  }, []);

  useEffect(() => {
    //console.log("location.state?.studentKey:", location.state?.studentKey);
    if (location.state?.studentKey && studentOptions.length > 0 && !lockedStudent) {
      const match = studentOptions.find(
        (opt) => String(opt.value) === String(location.state.studentKey)
      );
      if (match) {
        setFilterStudent(match);
        setLockedStudent(true);
        fetchInvoices(match.value);
      }
    }
  }, [studentOptions, location.state, lockedStudent]);

  useEffect(() => {
    // Reset selection/late fees whenever the underlying list changes so we
    // never hold stale row state (e.g. an invoice that just got paid).
    setSelected({});
    setLateFees({});
    setBulkLateFee("");
  }, [invoices.length]);

  const handleStudentChange = (option) => {
    setFilterStudent(option);
    fetchInvoices(option?.value || null);
  };

  const toggleRow = (pk_ps_key) => {
    setSelected((prev) => ({ ...prev, [pk_ps_key]: !prev[pk_ps_key] }));
  };

  const toggleSelectAll = (checked) => {
    const next = {};
    filteredInvoices.forEach((row) => {
      next[row.pk_ps_key] = checked;
    });
    setSelected(next);
  };

  const handleLateFeeChange = (pk_ps_key, value) => {
    // allow empty string while typing; treat as 0 everywhere it's used
    setLateFees((prev) => ({ ...prev, [pk_ps_key]: value }));
  };

  const applyBulkLateFee = () => {
    const selectedKeys = Object.keys(selected).filter((k) => selected[k]);
    if (selectedKeys.length === 0) {
      toast.info("Select at least one invoice first.");
      return;
    }
    const value = bulkLateFee === "" ? "0" : bulkLateFee;
    setLateFees((prev) => {
      const next = { ...prev };
      selectedKeys.forEach((k) => {
        next[k] = value;
      });
      return next;
    });
    toast.success(`Applied late fee ${value} to ${selectedKeys.length} selected invoice(s).`);
  };

  const selectedRows = useMemo(
    () => invoices.filter((row) => selected[row.pk_ps_key]),
    [invoices, selected]
  );

  const totals = useMemo(() => {
    let totalPending = 0;
    let totalLateFee = 0;
    selectedRows.forEach((row) => {
      const pending = Number(row?.ps_status) == 0 && Number(row?.is_part_payment) == 0
                  ? toNumber(row.ps_amount)
                  : row.ps_due_amount && row.ps_due_amount != "" && row.rp_type != 4
                  ? toNumber(row.ps_due_amount)
                  : 0.00
      totalPending += pending;
      totalLateFee += toNumber(lateFees[row.pk_ps_key] ?? 0);
    });
    return {
      totalPending,
      totalLateFee,
      totalDue: totalPending + totalLateFee,
    };
  }, [selectedRows, lateFees]);

  const allSelected = filteredInvoices.length > 0 && filteredInvoices.every((row) => selected[row.pk_ps_key]);

  const openConfirm = () => {
    if (selectedRows.length === 0) {
      toast.info("Select at least one invoice to pay.");
      return;
    }
    // Generate the idempotency ref once per batch; it stays the same across
    // retries of this exact confirm dialog so a resubmit after a network
    // error can never double-charge (backend step 14).
    setPaymentRef(generateTransactionRef());
    setPaymentMethod("offline");
    setOfflinePkCpmKey(null);
    setOfflineTransactionId("");
    setShowConfirm(true);
  };

  const closeConfirm = () => {
    if (submitting) return; // don't let them close mid-submit
    setShowConfirm(false);
  };

  // Offline requires a payment mode + transaction ID before Pay is enabled.
  // Online has nothing extra to fill in — the card on file is charged server-side.
  const canSubmitPayment =
    paymentMethod === "online" ||
    (paymentMethod === "offline" && !!offlinePkCpmKey && offlineTransactionId.trim() !== "");

  const handleConfirmPay = async () => {
    if (!canSubmitPayment) {
      toast.info("Select a payment mode and enter the transaction ID first.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        security_key: security_key,
        mode: "processPendingPaymentWithLateFee",
        fk_cent_key: sessionStorage.getItem("key"),
        fk_student_key: filterStudent?.value || selectedRows[0]?.fk_student_key || "",
        transaction_ref: paymentRef,
        total_paid_amount: Number(totals.totalDue.toFixed(2)),
        // 1 = Online (server charges the card on file via Stripe),
        // 2 = Offline (staff-recorded payment mode + transaction ID).
        payment_method: paymentMethod === "online" ? 1 : 2,
        pk_cpm_key_e: paymentMethod === "offline" ? offlinePkCpmKey?.value : "",
        eps_transaction_id: paymentMethod === "offline" ? offlineTransactionId.trim() : "",
        invoices: selectedRows.map((row) => ({
          pk_ps_key: row.pk_ps_key,
          total_pending_amount: Number(row?.ps_status) == 0 && Number(row?.is_part_payment) == 0
                  ? toNumber(row.ps_amount)
                  : row.ps_due_amount && row.ps_due_amount != "" && row.rp_type != 4
                  ? toNumber(row.ps_due_amount)
                  : 0.00,
          late_fee_amount: Number(toNumber(lateFees[row.pk_ps_key] ?? 0).toFixed(2)),
        })),
      };

      const response = await axios.post(BASE_URL + "API/api_index.php", payload);
      const { res, message } = response.data?.data || {};

      if (res === 1) {
        toast.success(message || "Payment successful. All selected invoices are now paid.", { autoClose: 4000 });
        setShowConfirm(false);
        setPaymentRef(null);
        fetchInvoices(filterStudent?.value || null);
      } else if (res === 2) {
        toast.info(message || "This payment was already processed.", { autoClose: 4000 });
        setShowConfirm(false);
        setPaymentRef(null);
        fetchInvoices(filterStudent?.value || null);
      } else {
        // res === 0 or -1: keep the modal open and the SAME paymentRef, so
        // the user can just hit "Pay" again without any duplicate-processing risk.
        toast.error(message || "Payment failed. Please try again.", { autoClose: 5000 });
      }
    } catch (error) {
      console.error("Error processing pending payment with late fee:", error);
      toast.error(
        error.response?.data?.message || "Unable to process payment right now. Please try again.",
        { autoClose: 5000 }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleClearFilters = () => {
  setFilterStudent(null);
  setFilterPaymentMethod(null);
  setFilterStartDate("");
  setFilterEndDate("");
  fetchInvoices(null); // student is the only filter that's server-side (changes the API call)
}; 


  return (
    <div className="">
      <ToastContainer position="top-center" />
      {/* --------------Page Start--------------- */}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <h5 className="page-title">Pending Payment with Late Fee</h5>
          <div role="presentation">
            <Breadcrumbs aria-label="breadcrumb" className="my-breadcrumbs">
              <NavLink underline="hover" color="inherit" to="/calendar">
                {" "}
                Home
              </NavLink>
              <NavLink underline="hover" color="inherit" to="/reports">
                {" "}
                Reports
              </NavLink>
              <Typography color="text.primary">Pending Payment with Late Fee</Typography>
            </Breadcrumbs>
          </div>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Buttonmui variant="outlined" onClick={openProcessLog}>
            <i className="fa-solid fa-clock-rotate-left mr-2 me-2"></i> Process Log
          </Buttonmui>
        </div>
      </div>

      <Card className="card my-card mb-3">
        <Card.Body>
          {/* -------- Filters + bulk late fee -------- */}
          <Row className="mb-3 align-items-center g-2">
            <Col xs={12} md={3}>
              <label className="form-label small mb-1">Student</label>
              <Select
                options={studentOptions}
                value={filterStudent}
                onChange={handleStudentChange}
                isClearable={!lockedStudent}
                isDisabled={lockedStudent}
                placeholder="All students"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={12} md={3}>
              <label className="form-label small mb-1">Payment Method</label>
              <Select
                options={paymentMethodOptions}
                value={filterPaymentMethod}
                onChange={setFilterPaymentMethod}
                isClearable
                placeholder="All methods"
                menuPortalTarget={document.body}
                styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>

            <Col xs={12} md={3}>
              <label className="form-label small mb-1">From Date</label>
              <input
                type="date"
                className="form-control mb-0"
                value={filterStartDate}
                max={filterEndDate || undefined}
                onChange={(e) => setFilterStartDate(e.target.value)}
              />
            </Col>

            <Col xs={12} md={3}>
              <label className="form-label small mb-1">To Date</label>
              <input
                type="date"
                className="form-control mb-0"
                value={filterEndDate}
                min={filterStartDate || undefined}
                onChange={(e) => setFilterEndDate(e.target.value)}
              />
            </Col>
            </Row>
          <Row className="mb-3 align-items-center g-2">
            <Col xs={12} md={4}>
              <label className="form-label small mb-1">
                Common Late Fee <span className="text-muted">(applies to selected rows)</span>
              </label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0"
                  value={bulkLateFee}
                  onChange={(e) => setBulkLateFee(e.target.value)}
                  style={{marginBottom: 'auto'}}
                />
                <Buttonmui variant="outlined" onClick={applyBulkLateFee} style={{height: 'fit-content'}}>
                  Apply
                </Buttonmui>
              </div>
            </Col>

            <Col xs={12} md={8} className="d-flex justify-content-md-end">
              <div className="text-end">
                <div className="small text-muted">
                  Selected: {selectedRows.length} invoice{selectedRows.length === 1 ? "" : "s"}
                </div>
                <div>
                  <strong>
                    Total Due: {totals.totalDue.toFixed(2)} {currency}
                  </strong>
                </div>
              </div>
            </Col>
          </Row>

          {loading && <LinearProgress className="mb-2" />}

          <Table responsive bordered hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Student</th>
                <th>Session / Product</th>
                <th>Payment Method</th>
                <th>Due Date</th>
                <th className="text-end">Pending Amount ({currency})</th>
                <th className="text-end">Late Fee ({currency})</th>
                <th className="text-end">Total Due ({currency})</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-3">
                    No pending invoices found.
                  </td>
                </tr>
              )}
              {filteredInvoices.map((row) => {
                const isChecked = !!selected[row.pk_ps_key];
                const lateFee = toNumber(lateFees[row.pk_ps_key] ?? 0);
               // const pending = toNumber(row.ps_amount);
                const pending = Number(row?.ps_status) == 0 && Number(row?.is_part_payment) == 0
                  ? toNumber(row.ps_amount)
                  : row.ps_due_amount && row.ps_due_amount != "" && row.rp_type != 4
                  ? toNumber(row.ps_due_amount)
                  : 0.00
                return (
                  <tr key={row.pk_ps_key}>
                    <td>
                      <Form.Check
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleRow(row.pk_ps_key)}
                      />
                    </td>
                    <td>
                      {row.st_first_name || row.st_surname
                        ? `${row.st_first_name || ""} ${row.st_surname || ""}`.trim()
                        : "-"}
                    </td>
                    <td>
                      {row.sest_name || "-"}
                      {row.pp_name ? <div className="small text-muted">{row.pp_name}</div> : null}
                    </td>
                    <td>{row.pp_pay_method == "1" ? "Online" : row.pp_pay_method == "2" ? "Offline" : "-"}</td>
                    <td>{row.ps_date ? Moment(row.ps_date, "YYYY-MM-DD").format("DD-MM-YYYY") : "-"}</td>
                    <td className="text-end">{pending.toFixed(2)}</td>
                    <td className="text-end" style={{ maxWidth: 130 }}>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        size="sm"
                        className="mb-0"
                        // Requirement #2: input box, default 0, editable per row
                        // even after a bulk-apply, so staff can fine-tune one row.
                        value={lateFees[row.pk_ps_key] ?? "0"}
                        disabled={!isChecked}
                        onChange={(e) => handleLateFeeChange(row.pk_ps_key, e.target.value)}
                      />
                    </td>
                    <td className="text-end">
                      <strong>{(pending + (isChecked ? lateFee : 0)).toFixed(2)}</strong>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>

          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="contained"
              className="primary-filled"
              disabled={selectedRows.length === 0}
              onClick={openConfirm}
            >
              Pay {totals.totalDue.toFixed(2)} {currency}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* -------- Confirm & Pay modal -------- */}
      <Modal show={showConfirm} onHide={closeConfirm} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="page-title mb-0">Confirm Payment</h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Table size="sm" bordered className="mb-3">
            <thead>
              <tr>
                <th>Session / Product</th>
                <th className="text-end">Pending</th>
                <th className="text-end">Late Fee</th>
                <th className="text-end">Total Due</th>
              </tr>
            </thead>
            <tbody>
              {selectedRows.map((row) => {
                //const pending = toNumber(row.ps_amount);
                const pending = Number(row?.ps_status) == 0 && Number(row?.is_part_payment) == 0
                  ? toNumber(row.ps_amount)
                  : row.ps_due_amount && row.ps_due_amount != "" && row.rp_type != 4
                  ? toNumber(row.ps_due_amount)
                  : 0.00
                const lateFee = toNumber(lateFees[row.pk_ps_key] ?? 0);
                return (
                  <tr key={row.pk_ps_key}>
                    <td>{row.sest_name || row.pp_name || row.pk_ps_key}</td>
                    <td className="text-end">{pending.toFixed(2)}</td>
                    <td className="text-end">{lateFee.toFixed(2)}</td>
                    <td className="text-end">{(pending + lateFee).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <th>Totals</th>
                <th className="text-end">{totals.totalPending.toFixed(2)}</th>
                <th className="text-end">{totals.totalLateFee.toFixed(2)}</th>
                <th className="text-end">{totals.totalDue.toFixed(2)}</th>
              </tr>
            </tfoot>
          </Table>
          <p className="text-muted small mb-3">
            The customer must pay exactly <strong>{totals.totalDue.toFixed(2)} {currency}</strong>. This amount
            will be added to the wallet and immediately allocated across the {selectedRows.length} selected
            invoice{selectedRows.length === 1 ? "" : "s"} above, oldest due date first. Each invoice will be
            marked as Paid and the wallet balance will return to $0.
          </p>

          {/* -------- Payment Method: Online (Stripe) or Offline (manual) -------- */}
          <Card className="rounded-1 card-bdr">
            <Card.Body>
              <h6 className="form-lable required-field mb-2">Payment Method</h6>
              <div className="d-flex gap-4 mb-3">
                <Form.Check
                  type="radio"
                  id="pay-method-offline"
                  name="pay-method"
                  label="Offline"
                  checked={paymentMethod === "offline"}
                  onChange={() => setPaymentMethod("offline")}
                  disabled={submitting}
                />
                {selectedRows.length > 0 && selectedRows?.[0]?.online_payment_verified > 0 ? <Form.Check
                  type="radio"
                  id="pay-method-online"
                  name="pay-method"
                  label="Online"
                  checked={paymentMethod === "online"}
                  onChange={() => setPaymentMethod("online")}
                  disabled={submitting}
                /> : null}
              </div>

              {paymentMethod === "online" ? (
                <p className="text-muted small mb-0">
                  The amount will be charged to the student&apos;s via Stripe. Once the charge
                  succeeds, it is added to the wallet and allocated to the selected invoices automatically.
                </p>
              ) : (
                <Row className="g-2">
                  <Col xs={12} md={6}>
                    <h6 className="form-lable required-field">Payment Mode</h6>
                    <Select
                      menuPlacement="auto"
                      options={offlineModesOptions}
                      value={offlinePkCpmKey}
                      onChange={setOfflinePkCpmKey}
                      placeholder="Select payment mode"
                      isDisabled={submitting}
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </Col>
                  <Col xs={12} md={6}>
                    <h6 className="form-lable required-field">Transaction ID</h6>
                    <Form.Control
                      type="text"
                      value={offlineTransactionId}
                      onChange={(e) => setOfflineTransactionId(e.target.value)}
                      disabled={submitting}
                      placeholder="e.g. receipt / reference number"
                    />
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="contained" className="primary-gray" onClick={closeConfirm} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            className="primary-filled"
            onClick={handleConfirmPay}
            disabled={submitting || !canSubmitPayment}
          >
            {submitting
              ? "Processing..."
              : paymentMethod === "online"
              ? `Charge ${totals.totalDue.toFixed(2)} ${currency} (Online)`
              : `Confirm ${totals.totalDue.toFixed(2)} ${currency} (Offline)`}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* -------- Process Log viewer -------- */}
      <Modal show={showProcessLog} onHide={() => setShowProcessLog(false)} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <h5 className="page-title mb-0">
              Process Log
              {filterStudent ? (
                <span className="text-muted fw-normal"> — {filterStudent.label}</span>
              ) : (
                <span className="text-muted fw-normal"> — All students</span>
              )}
            </h5>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loadingProcessLog && <LinearProgress className="mb-2" />}
          <Table responsive bordered hover size="sm" className="align-middle">
            <thead>
              <tr>
                <th>Processed On</th>
                <th>Student Name</th>
                <th>Payment Method</th>
                <th>Status</th>
                <th className="text-end"># Invoices</th>
                <th className="text-end">Pending ({currency})</th>
                <th className="text-end">Late Fee ({currency})</th>
                <th className="text-end">Total Paid ({currency})</th>
                <th className="text-end">Wallet Balance ({currency})</th>
                <th>Transaction Reference</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {processLogs.length === 0 && !loadingProcessLog && (
                <tr>
                  <td colSpan={11} className="text-center text-muted py-3">
                    No process log entries found.
                  </td>
                </tr>
              )}
              {processLogs.map((log) => (
                <tr key={log.pk_log_id}>
                  <td>
                    {(() => {
                      const timezoneIdentifier =
                        sessionStorage.getItem("timezone_identifier") || "Europe/London";

                      if (log.processed_at) {
                        return Moment.utc(log.processed_at)
                          .tz(timezoneIdentifier)
                          .format("DD-MM-YYYY HH:mm:ss");
                      }

                      if (log.created_on) {
                        return Moment.utc(log.created_on)
                          .tz(timezoneIdentifier)
                          .format("DD-MM-YYYY HH:mm:ss");
                      }

                      return "-";
                    })()}
                  </td>

                  <td>
                    {log.st_first_name || log.st_surname
                      ? `${log.st_first_name || ""} ${log.st_surname || ""}`.trim()
                      : "-"}
                  </td>
                  <td>{log.payment_method || "-"}</td>
                  <td>
                    <span
                      className={`badge ${
                        log.status == "1"
                          ? "cus-badge-Success"
                          : log.status == "2"
                          ? "cus-badge-danger"
                          : "cus-badge-danger"
                      }`}
                    >
                      {log.status == "1" ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                  <td className="text-end">{log.invoice_count ?? 0}</td>
                  <td className="text-end">{Number(log.total_pending_amount || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(log.total_late_fee || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(log.total_paid_amount || 0).toFixed(2)}</td>
                  <td className="text-end">{Number(log.remaining_wallet_balance || 0).toFixed(2)}</td>
                  <td className="small">{log.transaction_reference || "-"}</td>
                  <td className="small text-muted">{log.status == "1"
                          ? log.remarks || "-" : log.stripe_status || "Pending"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="contained" className="primary-gray" onClick={() => setShowProcessLog(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* --------------Page End--------------- */}
    </div>
  );
}

export default ReportFinancePendingPaymentLateFee;